import { Button, Grid, TextField, Theme, Typography } from '@mui/material';
import type { SxProps } from '@mui/material/styles';
import { useOrganizationFeatureDialog } from 'features/organizations/hooks/useOrganizationFeatureDialog';
import { OrganizationPricing } from 'features/organizations/organizations.types';
import { ChangeEvent } from 'react';
import {
  Control,
  Controller,
  FormState,
  FieldPath,
  ControllerRenderProps,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatToUSDollars } from 'shared/utils';

interface OrganizationSeatsFormValues {
  regularSeats: number;
  proSeats: number;
}

type OrganizationSeatsFormFieldsProps<T extends OrganizationSeatsFormValues> = {
  control: Control<T>;
  formState: FormState<OrganizationSeatsFormValues>;
  isDisabled?: boolean;
  pricing?: OrganizationPricing;
  showLearnMore?: boolean;
};

const learnMoreButtonSx: SxProps<Theme> = {
  fontSize: ({ typography }: Theme) => typography.caption.fontSize,
  fontWeight: 600,
  textDecoration: 'underline',
  textTransform: 'none',
  p: 0,
  minWidth: 0,
  justifyContent: 'flex-start',
};

export const OrganizationSeatsFormFields = <
  T extends OrganizationSeatsFormValues,
>(
  props: OrganizationSeatsFormFieldsProps<T>,
): JSX.Element => {
  const {
    control,
    formState,
    isDisabled = false,
    pricing,
    showLearnMore,
  } = props;
  const { errors } = formState;
  const { t } = useTranslation();
  const { openDialog, FeatureDialog } = useOrganizationFeatureDialog();

  const handleChange = (
    field: ControllerRenderProps<T, FieldPath<T>>,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    let { value } = e.target;

    // strip any decimal parts if present
    if (value.includes('.')) {
      [value] = value.split('.');
    }

    if (value === '') {
      field.onChange('');
      return;
    }

    // Ensure the value between 0 and 100000
    const numValue = Math.min(Math.max(0, Number(value) || 0), 100000);
    field.onChange(numValue);
  };

  return (
    <>
      {FeatureDialog}
      <Grid container size={12}>
        <Grid size={8}>
          <Typography variant="h6">
            {t('organizationPurchase.member')}
          </Typography>
          {pricing && (
            <Typography variant="body2">
              {t('organizationPurchase.priceStartingAt', {
                price: formatToUSDollars(
                  pricing.seats.regular[0].unitAmount,
                  2,
                ),
              })}
            </Typography>
          )}
        </Grid>
        <Grid size={4}>
          <Controller
            name={'regularSeats' as FieldPath<T>}
            control={control}
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <TextField
                  {...rest}
                  inputRef={ref}
                  type="number"
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      max: 100000,
                      inputMode: 'numeric',
                      'data-testid': 'regular-seats-input',
                    },
                  }}
                  error={Boolean(errors?.regularSeats)}
                  helperText={errors.regularSeats?.message}
                  fullWidth
                  disabled={isDisabled || formState.isSubmitting}
                  onChange={(e) => handleChange(field, e)}
                />
              );
            }}
          />
        </Grid>
      </Grid>
      {showLearnMore && (
        <Grid>
          <Button
            type="button"
            variant="text"
            size="small"
            color="error"
            disabled={isDisabled || formState.isSubmitting}
            sx={learnMoreButtonSx}
            onClick={() => openDialog('regular')}
          >
            {t('organizationPurchase.learnMore')}
          </Button>
        </Grid>
      )}
      <Grid container size={12} alignItems="center">
        <Grid size={8}>
          <Typography variant="h6">{t('organizationPurchase.pro')}</Typography>
          {pricing && (
            <Typography variant="body2">
              {t('organizationPurchase.priceStartingAt', {
                price: formatToUSDollars(pricing.seats.pro[0].unitAmount, 2),
              })}
            </Typography>
          )}
        </Grid>
        <Grid size={4}>
          <Controller
            name={'proSeats' as FieldPath<T>}
            control={control}
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <TextField
                  {...rest}
                  inputRef={ref}
                  type="number"
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      max: 100000,
                      inputMode: 'numeric',
                      'data-testid': 'pro-seats-input',
                    },
                  }}
                  error={Boolean(errors?.proSeats)}
                  helperText={errors.proSeats?.message}
                  fullWidth
                  disabled={isDisabled || formState.isSubmitting}
                  onChange={(e) => handleChange(field, e)}
                />
              );
            }}
          />
        </Grid>
      </Grid>
      {showLearnMore && (
        <Grid>
          <Button
            type="button"
            variant="text"
            size="small"
            color="error"
            disabled={isDisabled || formState.isSubmitting}
            sx={learnMoreButtonSx}
            onClick={() => openDialog('pro')}
          >
            {t('organizationPurchase.learnMore')}
          </Button>
        </Grid>
      )}
    </>
  );
};
